// Root's boot-error mapping for the cross-account Bridge link (ENG-4685).
//
// The account-first boot decides `access_empty` when the local Bridge is
// linked to a Studio the signed-in account cannot see: account A linked this
// machine, the user logged out and signed in as account B. Before this module
// the boot threw a generic ApiError and Root rendered "Unable to load account.
// Please try again." with a reload-only button, which re-entered the identical
// state. The boot now throws a typed error and Root maps it to the ENG-4166 D3
// copy plus a Sign-out escape, so the screen names the actual cause and offers
// a way out when the sidecar unlink could not clear the link.
//
// Pure module: no React, no window, so the mapping is unit-testable and Root
// stays a thin producer. Per rules: code-comments-required,
// reference-rules-in-comments, think-in-systems (ai.knowledge/global).
import { ApiError } from '#~/api/types.js';
import type { BootError } from '#~/depot/types.js';
import { makeCrossAccountBootError } from './root-room-sync.js';

/** Generic copy for every other boot failure; the real cause rides in `detail`. */
export const GENERIC_BOOT_ERROR_MESSAGE = 'Unable to load account. Please try again.';

/**
 * Thrown by the boot generator when the local Bridge is linked to a Studio the
 * account cannot access and the in-place sidecar unlink did not recover it.
 */
export class CrossAccountBootError extends ApiError {
  public readonly agentId: string | undefined;

  public constructor(agentId: string | undefined) {
    super(
      `This Bridge${agentId === undefined ? '' : ` (${agentId})`} is linked to a Studio this account cannot access.`
    );
    this.agentId = agentId;
  }
}

export const isCrossAccountBootError = (error: unknown): error is CrossAccountBootError =>
  error instanceof CrossAccountBootError;

/**
 * Map a failed boot to the BootError Root publishes. Cross-account failures get
 * the D3 copy and the Sign-out escape; everything else keeps the generic copy
 * with the underlying message as detail.
 */
export const toRootBootError = (
  error: Error,
  onRetry: () => void,
  onSignOut: () => void
): BootError =>
  isCrossAccountBootError(error)
    ? makeCrossAccountBootError(onRetry, onSignOut)
    : {
        message: GENERIC_BOOT_ERROR_MESSAGE,
        detail: error.message,
        severity: 'retryable',
        retryFn: onRetry
      };
